import React, { useMemo } from 'react';
import { useFeatures, FeatureFlags } from '../../hooks/useFeatures';

/**
 * Knowledge Base tab identifiers. `playbook` is the live discovery checklist;
 * everything from `objections` down belongs to the older live-intelligence
 * model that Lushin replaced.
 */
export type KnowledgeBaseTabId =
  | 'companyContext'
  | 'playbook'
  | 'objections'
  | 'faqs'
  | 'landmines'
  | 'competitors'
  | 'salesplaybooks';

interface TabDescriptor {
  id: KnowledgeBaseTabId;
  label: string;
  /** Capability flag this tab needs. Omitted means always visible. */
  requires?: keyof Omit<FeatureFlags, never>;
  /** Preserved so the existing product tour still finds its anchors. */
  tutorialId: string;
}

/**
 * Declared once rather than as six near-identical buttons, so gating a tab is a
 * one-line change and no tab can drift out of style with its neighbours.
 *
 * The objection / FAQ / landmine / competitor / sales-playbook tabs are gated on
 * `liveIntelligence`, which is off for Lushin: the discovery checklist REPLACES
 * that model here. The code and data stay intact behind the flag rather than
 * being deleted — re-enabling is a flag flip.
 */
const TABS: readonly TabDescriptor[] = [
  { id: 'companyContext', label: 'Company Context', tutorialId: 'tab-company-context' },
  { id: 'playbook', label: 'Discovery Playbook', requires: 'livePlaybook', tutorialId: 'tab-playbook' },
  { id: 'objections', label: 'Objections', requires: 'liveIntelligence', tutorialId: 'tab-objections' },
  { id: 'faqs', label: 'FAQs', requires: 'liveIntelligence', tutorialId: 'tab-faqs' },
  { id: 'landmines', label: 'Landmines', requires: 'liveIntelligence', tutorialId: 'tab-landmines' },
  { id: 'competitors', label: 'Competitors', requires: 'liveIntelligence', tutorialId: 'tab-competitors' },
  { id: 'salesplaybooks', label: 'Sales Playbook', requires: 'liveIntelligence', tutorialId: 'tab-salesplaybooks' }
];

interface KnowledgeBaseTabsProps {
  activeTab: KnowledgeBaseTabId;
  onTabChange: (tab: KnowledgeBaseTabId) => void;
  disabled?: boolean;
}

/**
 * Returns the tabs visible for the current capability flags, plus a safe
 * default. Exported so the page can correct an active tab that a flag hid —
 * otherwise a tenant with `liveIntelligence` off would land on the default
 * `objections` tab and see an empty page with no way back.
 */
export function useVisibleKnowledgeBaseTabs(): {
  visibleTabs: TabDescriptor[];
  defaultTab: KnowledgeBaseTabId;
  loading: boolean;
} {
  const features = useFeatures();
  // Memoised because callers put `visibleTabs` in effect dependency arrays; a
  // fresh array every render would re-run those effects on every render.
  return useMemo(() => {
    const visibleTabs = TABS.filter((tab) => !tab.requires || features?.[tab.requires]);
    // Prefer the checklist when it is on: for Lushin it is the KB's whole point.
    const defaultTab: KnowledgeBaseTabId = features?.livePlaybook
      ? 'playbook'
      : (visibleTabs?.[0]?.id ?? 'companyContext');
    return { visibleTabs, defaultTab, loading: features?.loading ?? false };
  }, [features]);
}

const KnowledgeBaseTabs: React.FC<KnowledgeBaseTabsProps> = ({
  activeTab,
  onTabChange,
  disabled = false
}) => {
  const { visibleTabs } = useVisibleKnowledgeBaseTabs();

  return (
    <div className="border-b border-gray-200 mb-4">
      <nav className="-mb-px flex space-x-8" data-tutorial="knowledge-tabs" role="tablist">
        {visibleTabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onTabChange(tab.id)}
              disabled={disabled}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                isActive
                  ? 'border-plasma-cyan text-obsidian'
                  : 'border-transparent text-gray-500 hover:text-obsidian hover:border-gray-300'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-tutorial={tab.tutorialId}
            >
              {tab.label}
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default KnowledgeBaseTabs;
